<template>
  <div class="range-element-fi">
    <range-slider-fi
      class="fi-px-6"
      :min="minValue"
      :max="maxValue"
      :value="[lower, upper]"
      :disabled="disabled"
      :interval="interval"
      @drag-end="onDragEndSlider"
      @change="onChangeSlider"
    />
    <div class="range-element-fi__text-input">
      <text-input-fi
        :model-value="lower"
        :label="`[${filter.unit}]`"
        :disabled="disabled"
        :min="minValue"
        :max="maxValue"
        data-test="range-value-lower"
        show-localized-values
        @change="onInputLower"
      />
      <div class="fi-px-12">-</div>
      <text-input-fi
        :model-value="upper"
        :label="`[${filter.unit}]`"
        :disabled="disabled"
        :min="minValue"
        :max="maxValue"
        data-test="range-value-upper"
        show-localized-values
        @change="onInputUpper"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { RangeSliderFi, TextInputFi } from 'atoms';
import { computed, onMounted, ref, watch } from 'vue';

import type { SearchFilter } from '@/components/search-fact-finder-results-fi/SearchFactFinderResultsFi.vue';

defineOptions({
  name: 'RangeElementFi',
});

const props = defineProps<{
  option: any;
  filter: any;
}>();

const emits = defineEmits<{
  change: [option: SearchFilter];
}>();

watch(
  () => props.option,
  (newValue) => {
    minValue.value = Math.floor(Number(newValue.absoluteMinValue));
    maxValue.value = Math.floor(Number(newValue.absoluteMaxValue));
    lower.value = newValue.selectedMinValue;
    upper.value = newValue.selectedMaxValue;
  },
);

const lower = ref(0);
const upper = ref(0);
const minValue = ref(Math.floor(Number(props.option.absoluteMinValue)));
const maxValue = ref(Math.ceil(Number(props.option.absoluteMaxValue)));

const disabled = computed(() => props.option.disabled || minValue.value + 1 >= maxValue.value);

const interval = computed(() => {
  const { decimalPlaces } = props.filter;
  if (!decimalPlaces) {
    return 1;
  }
  return 10 ** -decimalPlaces;
});

onMounted(() => {
  // select rounded values when fact-finder returns default outer ranges
  lower.value =
    props.option.selectedMinValue === props.option.absoluteMinValue
      ? minValue.value
      : Number(props.option.selectedMinValue);
  upper.value =
    props.option.selectedMaxValue === props.option.absoluteMaxValue
      ? maxValue.value
      : Number(props.option.selectedMaxValue);
});

const onDragEndSlider = (newValue: number | number[] | null) => {
  if (!Array.isArray(newValue) || !newValue.length) {
    console.error('invalid newValue', newValue);
    return;
  }
  [lower.value, upper.value] = newValue;

  emitChange();
};

const onChangeSlider = (newValue: number | number[]) => {
  if (Array.isArray(newValue)) {
    [lower.value, upper.value] = newValue;
  }
};

const onInputLower = (newValueInput: string) => {
  let newValue = Number(newValueInput);

  if (newValue > maxValue.value) {
    newValue = maxValue.value;
  }

  if (newValue < minValue.value) {
    newValue = minValue.value;
  }

  if (newValue > upper.value) {
    newValue = upper.value;
  }

  lower.value = newValue;
  emitChange();
};

const onInputUpper = (newValueInput: string) => {
  let newValue = Number(newValueInput);

  if (newValue > maxValue.value) {
    newValue = maxValue.value;
  }

  if (newValue < minValue.value) {
    newValue = minValue.value;
  }

  if (newValue < lower.value) {
    newValue = lower.value;
  }

  upper.value = newValue;
  emitChange();
};

const emitChange = () => {
  if (
    lower.value === props.option.absoluteMinValue &&
    upper.value === props.option.absoluteMaxValue
  ) {
    emits('change', {
      value: false,
      element: {
        name: props.option.text,
        associatedFieldName: props.option.associatedFieldName,
        selectedMinValue: String(lower.value),
        selectedMaxValue: String(upper.value),
        selected: false,
      },
    });
  } else {
    emits('change', {
      value: true,
      element: {
        name: props.option.text,
        associatedFieldName: props.option.associatedFieldName,
        selectedMinValue: String(lower.value),
        selectedMaxValue: String(upper.value),
        selected: true,
      },
    });
  }
};
</script>

<style scoped src="./range-element-fi" />
